import { useMemo } from 'react';
import { flatten } from 'lodash';
import { useInfiniteQuery } from 'react-query';
import * as api from '../../services/api';
import useDebounce from '../useDebounce';
import { SORT_BY_NAME } from '../../utils/sorting';
import useAccount from './useAccount';

const SORTER_MAP = {
  1: 2,
  2: 2,
  3: 0,
  4: 0,
  5: 1
};

const useAvailableEmployeeByTimeAndPosition = ({
  enabled,
  pageSize = 50,
  search,
  employeesBlacklist = [],
  branchId,
  startTime,
  endTime,
  positionId,
  shiftId,
  projectId,
  onlyActiveEmployees = true
}) => {
  const debounceSearch = useDebounce(search, 300);
  const { accountId } = useAccount();

  const storeKey = [
    'availableEmployeesByTimeAndPosition',
    accountId,
    branchId,
    startTime,
    endTime,
    positionId,
    shiftId,
    projectId,
    debounceSearch
  ].filter(Boolean);

  const { isLoading, data, fetchNextPage, hasNextPage } = useInfiniteQuery(
    storeKey,

    async ({ pageParam }) => {
      const response = await api.fetchAvailableEmployeesByTimeAndPosition({
        accountId,
        branchId,
        startTime,
        endTime,
        positionId,
        name: debounceSearch,
        pageSize,
        token: pageParam,
        shiftId,
        projectId,
        onlyActiveEmployees
      });

      return response?.data;
    },
    {
      enabled,
      staleTime: 0,
      cacheTime: Infinity,
      getNextPageParam: (lastGroup, allGroups) => {
        const totalItems = allGroups.reduce((sum, { data: nextData }) => sum + nextData?.length, 0);
        return totalItems === lastGroup.total ? false : lastGroup.token;
      }
    }
  );

  // sort each page by list type to show red at the end (until sorted in BE)
  const employees = useMemo(
    () =>
      flatten(
        (data?.pages || []).map(({ data: pageData }) =>
          pageData
            .sort((a, b) => {
              const typeA = a.warnings.length > 0 ? 3 : a.listType;
              const typeB = b.warnings.length > 0 ? 3 : b.listType;

              if (SORTER_MAP[typeA] === SORTER_MAP[typeB]) {
                return SORT_BY_NAME(a, b);
              }

              return SORTER_MAP[typeB] - SORTER_MAP[typeA];
            })
            .filter(({ id }) => {
              return !employeesBlacklist?.includes(id);
            })
        )
      ),
    [data?.pages, employeesBlacklist]
  );

  return { isLoading, data: employees, fetchMore: fetchNextPage, canFetchMore: hasNextPage };
};

export default useAvailableEmployeeByTimeAndPosition;
